import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { api, Email, Account } from '../lib/api';
import TodoSidebar from './TodoSidebar';
import DailyBrief from './DailyBrief';
import AssistantChatPanel from './AssistantChatPanel';

export default function NewDashboard() {
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [emails, setEmails] = useState<Email[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Fetch accounts and emails on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [accountsData, emailsData] = await Promise.all([
          api.getAccounts(),
          api.getEmails({ limit: 50, offset: 0 })
        ]);
        
        setAccounts(accountsData.accounts);
        setEmails(emailsData.emails);
        
        if (accountsData.accounts.length > 0) {
          setSelectedAccountId(accountsData.accounts[0].id);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSelectEmail = (email: Email) => {
    setSelectedEmail(email);
    if (!email.isRead) {
      api.markAsRead(email.id).catch(console.error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">📧</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white hidden sm:inline">
                emailah
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={logout}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-red-500"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - 3 Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Todo Items */}
        <div className={`w-full lg:w-1/4 border-r border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col ${selectedEmail ? 'hidden lg:flex' : 'flex'}`}>
          <TodoSidebar
            emails={emails}
            selectedEmail={selectedEmail}
            onSelectEmail={handleSelectEmail}
            isLoading={isLoading}
          />
        </div>

        {/* Center - Daily Brief */}
        <div className={`flex-1 overflow-hidden flex flex-col ${selectedEmail && selectedEmail.isNewsletter ? 'flex' : 'hidden lg:flex'}`}>
          <DailyBrief
            selectedEmail={selectedEmail}
            emails={emails}
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            onSelectAccountId={setSelectedAccountId}
            onSelectEmail={handleSelectEmail}
            isLoading={isLoading}
          />
        </div>

        {/* Right Panel - Assistant Chat */}
        <div className="hidden lg:flex w-1/4 border-l border-gray-200 dark:border-gray-700 overflow-hidden">
          <AssistantChatPanel
            emailId={selectedEmail?.id || null}
            userName="Mick"
          />
        </div>
      </div>
    </div>
  );
}
